import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/api";

export function HkaBanner({ hka }) {
  const { tl } = useI18n();
  if (!hka) return null;
  if (!hka.configured) {
    return (
      <div data-testid="hka-banner" className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
        {tl({ es: "Factura HKA (Panamá) no configurada — añade tus credenciales para emitir facturas electrónicas.", en: "Factura HKA (Panama) not configured — add your credentials to emit e-invoices.", fr: "Factura HKA (Panama) non configurée — ajoutez vos identifiants pour émettre les factures électroniques." })}
      </div>
    );
  }
  return (
    <div data-testid="hka-status-banner" className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-primary">
      <span className="font-semibold">Factura HKA {hka.online ? "✓" : "⚠"}</span>
      <span className="text-muted-foreground">RUC {hka.ruc}</span>
      {hka.licencia && <span className="text-muted-foreground">{tl({ es: "Licencia", en: "License", fr: "Licence" })}: {hka.licencia}</span>}
      {hka.folios_disponibles != null && <span className="text-muted-foreground">{tl({ es: "Folios disponibles", en: "Available folios", fr: "Folios disponibles" })}: {hka.folios_disponibles}/{hka.folios_ciclo}</span>}
    </div>
  );
}

export function FinancialReportsCard({ rFrom, setRFrom, rTo, setRTo, reportUrl }) {
  const { tl } = useI18n();
  return (
    <div data-testid="financial-reports-card" className="mb-6 rounded-2xl border border-border bg-card p-5">
      <h2 className="font-display text-lg font-bold tracking-tight">{tl({ es: "Reportes financieros", en: "Financial reports", fr: "Rapports financiers" })}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{tl({ es: "Exporta ingresos y estadísticas por rango de fechas.", en: "Export revenue and stats by date range.", fr: "Exportez le chiffre d'affaires et les statistiques par plage de dates." })}</p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">{tl({ es: "Desde", en: "From", fr: "Du" })}</label>
          <Input type="date" data-testid="report-from" value={rFrom} onChange={(e) => setRFrom(e.target.value)} className="w-44" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">{tl({ es: "Hasta", en: "To", fr: "Au" })}</label>
          <Input type="date" data-testid="report-to" value={rTo} onChange={(e) => setRTo(e.target.value)} className="w-44" />
        </div>
        <a href={reportUrl("financial.csv")} data-testid="report-csv-btn" className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-accent">{tl({ es: "Exportar CSV", en: "Export CSV", fr: "Exporter CSV" })}</a>
        <a href={reportUrl("summary.pdf")} target="_blank" rel="noreferrer" data-testid="report-pdf-btn" className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">{tl({ es: "Reporte PDF", en: "PDF report", fr: "Rapport PDF" })}</a>
      </div>
    </div>
  );
}

export function PaymentsTable({ payments, onConfirm, onRefund, onEmit, onSendInvoice }) {
  const { t, tl } = useI18n();
  const methodLabel = (m) => ({
    paguelo_facil: tl({ es: "Pago en línea con tarjeta", en: "Online card payment", fr: "Paiement en ligne par carte" }),
    manual_transfer: tl({ es: "Transferencia", en: "Transfer", fr: "Virement" }),
    manual_cash: tl({ es: "Efectivo", en: "Cash", fr: "Espèces" }),
    manual_card: tl({ es: "Tarjeta en estudio", en: "Card in studio", fr: "Carte au studio" }),
    yappy: "Yappy",
  }[m] || m);
  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-secondary/40 text-left"><tr>
          <th className="p-3">{tl({ es: "Cliente", en: "Client", fr: "Client" })}</th>
          <th className="p-3">{tl({ es: "Documento", en: "Document", fr: "Document" })}</th>
          <th className="p-3">Monto</th><th className="p-3">Método</th><th className="p-3">Estado</th><th className="p-3">Factura HKA</th><th className="p-3"></th>
        </tr></thead>
        <tbody>
          {payments.map((p) => (
            <tr key={p.payment_id} data-testid={`payment-row-${p.payment_id}`} className="border-b border-border/60">
              <td className="p-3 font-medium" data-testid={`payment-client-${p.payment_id}`}>{p.client_name || "—"}</td>
              <td className="p-3 text-muted-foreground" data-testid={`payment-doc-${p.payment_id}`}>
                {p.id_number ? <span>{(p.id_type || "").toUpperCase()} {p.id_number}</span> : "—"}
              </td>
              <td className="p-3 font-medium">${p.amount}{p.is_renewal && <span className="ml-1 rounded bg-secondary px-1.5 py-0.5 text-[10px]">renovación</span>}</td>
              <td className="p-3 text-muted-foreground">{methodLabel(p.method)}</td>
              <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs ${p.status === "paid" ? "bg-primary/10 text-primary" : p.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-secondary"}`}>{p.status}</span></td>
              <td className="p-3">
                {p.hka_cufe
                  ? <span className="inline-flex items-center gap-1.5">
                      <span data-testid={`cufe-${p.payment_id}`} className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary" title={p.hka_cufe}>CUFE ✓</span>
                      <Button size="sm" variant="ghost" data-testid={`send-invoice-${p.payment_id}`} onClick={() => onSendInvoice(p.payment_id)} className="h-7 rounded-full px-2 text-xs" title={tl({ es: "Enviar factura por email", en: "Email invoice", fr: "Envoyer la facture par e-mail" })}>
                        {tl({ es: "Enviar", en: "Send", fr: "Envoyer" })}
                      </Button>
                    </span>
                  : p.status === "paid"
                    ? <Button size="sm" variant="outline" data-testid={`emit-invoice-${p.payment_id}`} onClick={() => onEmit(p.payment_id)} className="rounded-full">{tl({ es: "Emitir", en: "Emit", fr: "Émettre" })}</Button>
                    : <span className="text-xs text-muted-foreground">—</span>}
              </td>
              <td className="p-3 text-right">
                {p.status === "pending" && <Button size="sm" data-testid={`confirm-pay-${p.payment_id}`} onClick={() => onConfirm(p.payment_id)} className="rounded-full">{t("admin.confirm")}</Button>}
                {p.status === "paid" && <Button size="sm" variant="outline" data-testid={`refund-pay-${p.payment_id}`} onClick={() => onRefund(p.payment_id)} className="rounded-full">{t("admin.refund")}</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SubscriptionsTable({ subs, onAction }) {
  const { t, tl } = useI18n();
  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-secondary/40 text-left"><tr>
          <th className="p-3">Cliente</th><th className="p-3">Plan</th><th className="p-3">{tl({ es: "Próximo cobro", en: "Next charge", fr: "Prochain prélèvement" })}</th>
          <th className="p-3">{tl({ es: "Cobros", en: "Charges", fr: "Prélèvements" })}</th><th className="p-3">Estado</th><th className="p-3"></th>
        </tr></thead>
        <tbody>
          {subs.map((s) => (
            <tr key={s.subscription_id} data-testid={`sub-row-${s.subscription_id}`} className="border-b border-border/60">
              <td className="p-3 font-medium">{s.client_name}</td>
              <td className="p-3 text-muted-foreground">{tl(s.product_name)}</td>
              <td className="p-3 text-muted-foreground" data-testid={`sub-next-${s.subscription_id}`}>
                {(s.period_end || "").slice(0, 10)}
                {s.last_charge_error && <div className="mt-0.5 text-[11px] text-destructive" data-testid={`sub-error-${s.subscription_id}`}>⚠ {s.last_charge_error} ({s.charge_failures}/3)</div>}
              </td>
              <td className="p-3 text-muted-foreground" data-testid={`sub-charges-${s.subscription_id}`}>
                {s.charges_done || 1}/{s.max_charges || 12}
                <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] ${s.pf_cod_oper ? "bg-primary/10 text-primary" : "bg-secondary"}`}>
                  {s.pf_cod_oper ? tl({ es: "tarjeta auto", en: "auto card", fr: "carte auto" }) : tl({ es: "manual", en: "manual", fr: "manuel" })}
                </span>
              </td>
              <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs ${s.status === "active" ? "bg-primary/10 text-primary" : s.status === "frozen" ? "bg-amber-100 text-amber-700" : "bg-secondary"}`}>{s.status}</span></td>
              <td className="p-3 text-right">
                <div className="flex justify-end gap-1.5">
                  {s.status === "active" && s.pf_cod_oper && <Button size="sm" variant={s.last_charge_error ? "default" : "outline"} data-testid={`sub-charge-${s.subscription_id}`} onClick={() => onAction(s.subscription_id, "charge-now")} className="rounded-full">{s.last_charge_error ? tl({ es: "Reintentar cobro", en: "Retry charge", fr: "Réessayer" }) : tl({ es: "Cobrar ahora", en: "Charge now", fr: "Prélever" })}</Button>}
                  {s.status !== "cancelled" && s.status !== "completed" && <Button size="sm" variant="outline" data-testid={`sub-link-${s.subscription_id}`} onClick={() => onAction(s.subscription_id, "send-payment-link")} className="rounded-full" title={tl({ es: "Enviar al cliente un enlace de pago con tarjeta (nueva tarjeta)", en: "Email the client a card payment link (new card)", fr: "Envoyer au client un lien de paiement par carte (nouvelle carte)" })}>{tl({ es: "Enlace de pago", en: "Payment link", fr: "Lien de paiement" })}</Button>}
                  {s.status === "active" && <Button size="sm" variant="outline" data-testid={`sub-freeze-${s.subscription_id}`} onClick={() => onAction(s.subscription_id, "freeze")} className="rounded-full">Pausar</Button>}
                  {s.status === "frozen" && <Button size="sm" data-testid={`sub-unfreeze-${s.subscription_id}`} onClick={() => onAction(s.subscription_id, "unfreeze")} className="rounded-full">Reactivar</Button>}
                  {s.status !== "cancelled" && <Button size="sm" variant="ghost" data-testid={`sub-cancel-${s.subscription_id}`} onClick={() => onAction(s.subscription_id, "cancel")} className="rounded-full text-destructive">Cancelar</Button>}
                </div>
              </td>
            </tr>
          ))}
          {subs.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">{t("common.noData")}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
